import { useState, useEffect } from 'react';
import { BookOpen, ChevronLeft, ChevronRight, Sparkles, Heart, Shield, Zap, RotateCw, Check, Gem, ArrowLeft, Volume2, VolumeX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { motion, AnimatePresence } from 'framer-motion';
import { crystals } from '@/data/crystalData';
import { supabase } from '@/integrations/supabase/client';
import { useGameSounds } from '@/hooks/useGameSounds';

function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

interface CrystalFlashCardsProps {
  onBack?: () => void;
}

export function CrystalFlashCards({ onBack }: CrystalFlashCardsProps) {
  const [cards, setCards] = useState(shuffleArray([...crystals]));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [studiedCards, setStudiedCards] = useState<Set<string>>(new Set());
  const [xpEarned, setXpEarned] = useState(0);
  const { playSound, preloadSounds, soundsEnabled, toggleSounds } = useGameSounds();

  useEffect(() => {
    preloadSounds(['correct', 'cardFlip', 'celebration']);
  }, [preloadSounds]);

  const currentCrystal = cards[currentIndex];
  const progress = (studiedCards.size / crystals.length) * 100;

  const flipCard = () => {
    setIsFlipped(!isFlipped);
    playSound('cardFlip');
  };

  const nextCard = () => {
    if (currentIndex < cards.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setIsFlipped(false);
    }
  };

  const prevCard = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setIsFlipped(false);
    }
  };

  const markAsStudied = async () => {
    if (studiedCards.has(currentCrystal.id)) {
      nextCard();
      return;
    }

    const newStudied = new Set(studiedCards);
    newStudied.add(currentCrystal.id);
    setStudiedCards(newStudied);
    setXpEarned(xpEarned + 5);
    playSound('correct');
    
    // Check if all cards studied
    if (newStudied.size === crystals.length) {
      playSound('celebration');
    }

    // Save progress
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: existing } = await supabase
          .from('crystal_learning_progress')
          .select('*')
          .eq('user_id', user.id)
          .single();

        const today = new Date().toISOString().split('T')[0];
        const newXp = (existing?.xp_earned || 0) + 5;

        if (existing) {
          await supabase.from('crystal_learning_progress').update({
            xp_earned: newXp,
            last_quiz_date: today
          }).eq('user_id', user.id);
        } else {
          await supabase.from('crystal_learning_progress').insert({
            user_id: user.id,
            xp_earned: 5,
            last_quiz_date: today
          });
        }
      }
    } catch (error) {
      console.error('Error:', error);
    }

    nextCard();
  };

  const resetCards = () => {
    setCards(shuffleArray([...crystals]));
    setCurrentIndex(0);
    setIsFlipped(false);
    setStudiedCards(new Set());
    setXpEarned(0);
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'love': return Heart;
      case 'protection': return Shield;
      case 'confidence': case 'abundance': return Sparkles;
      default: return Zap;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-gradient-to-br from-primary/10 to-accent/10 border-primary/20">
        <CardContent className="p-4">
          {onBack && (
            <Button onClick={onBack} variant="ghost" size="sm" className="mb-3 -ml-2">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Learning Hub
            </Button>
          )}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-primary" />
              <span className="font-medium">Flash Cards</span>
            </div>
            <Badge variant="outline" className="text-primary border-primary/50">
              +{xpEarned} XP
            </Badge>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
            <span>{studiedCards.size} of {crystals.length} studied</span>
            <span>•</span>
            <span>Card {currentIndex + 1}/{cards.length}</span>
          </div>
          <Progress value={progress} className="h-2" />
        </CardContent>
      </Card>

      {/* Flash Card */}
      <div className="relative h-[400px] perspective-1000">
        <AnimatePresence mode="wait">
          <motion.div
            key={`${currentCrystal.id}-${isFlipped}`}
            initial={{ rotateY: isFlipped ? -90 : 90, opacity: 0 }}
            animate={{ rotateY: 0, opacity: 1 }}
            exit={{ rotateY: isFlipped ? 90 : -90, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0"
          >
            <Card 
              className={`h-full cursor-pointer transition-all hover:shadow-lg ${
                studiedCards.has(currentCrystal.id) ? 'ring-2 ring-accent/50' : ''
              }`}
              onClick={flipCard}
            >
              <CardContent className="h-full flex flex-col p-6">
                {!isFlipped ? (
                  // Front - Crystal Image and Name
                  <div className="flex-1 flex flex-col items-center justify-center text-center">
                    <div className="relative mb-4">
                      <img 
                        src={currentCrystal.image} 
                        alt={currentCrystal.name}
                        className="w-32 h-32 object-contain"
                      />
                      {studiedCards.has(currentCrystal.id) && (
                        <div className="absolute -top-2 -right-2 p-1 rounded-full bg-accent">
                          <Check className="h-4 w-4 text-accent-foreground" />
                        </div>
                      )}
                    </div>
                    <h3 className="text-2xl font-semibold text-foreground mb-2">
                      {currentCrystal.name}
                    </h3>
                    <div className="flex flex-wrap gap-1 justify-center mb-4">
                      {currentCrystal.chakras.map(chakra => (
                        <Badge key={chakra} variant="secondary" className="text-xs">
                          {chakra}
                        </Badge>
                      ))}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Tap to reveal properties →
                    </p>
                  </div>
                ) : (
                  // Back - Properties and Uses
                  <div className="flex-1 flex flex-col overflow-auto">
                    <div className="flex items-center gap-2 mb-4">
                      <Gem className="h-5 w-5 text-primary" />
                      <h3 className="text-lg font-semibold">{currentCrystal.name}</h3>
                    </div>

                    <div className="space-y-4 flex-1">
                      {/* Key Properties */}
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-2">KEY PROPERTIES</p>
                        <ul className="space-y-1">
                          {currentCrystal.properties.slice(0, 3).map((prop, i) => (
                            <li key={i} className="text-sm flex items-start gap-2">
                              <Sparkles className="h-3 w-3 text-primary mt-1 flex-shrink-0" />
                              {prop}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Best For */}
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-2">BEST FOR</p>
                        <div className="flex flex-wrap gap-1">
                          {currentCrystal.categories.slice(0, 4).map(cat => {
                            const Icon = getCategoryIcon(cat);
                            return (
                              <Badge key={cat} variant="outline" className="text-xs capitalize">
                                <Icon className="h-3 w-3 mr-1" />
                                {cat}
                              </Badge>
                            );
                          })}
                        </div>
                      </div>

                      {/* Quick Tip */}
                      {currentCrystal.categoryTaglines && Object.values(currentCrystal.categoryTaglines)[0] && (
                        <div className="p-3 bg-primary/10 rounded-lg">
                          <p className="text-xs font-medium text-primary mb-1">QUICK TIP</p>
                          <p className="text-sm">
                            {Object.values(currentCrystal.categoryTaglines)[0]}
                          </p>
                        </div>
                      )}
                    </div>

                    <p className="text-sm text-muted-foreground text-center mt-4">
                      ← Tap to see crystal
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between gap-4">
        <Button
          variant="outline"
          size="icon"
          onClick={prevCard}
          disabled={currentIndex === 0}
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>

        <div className="flex-1 flex gap-2">
          <Button
            variant="outline"
            onClick={resetCards}
            className="flex-1"
          >
            <RotateCw className="h-4 w-4 mr-2" />
            Shuffle
          </Button>
          <Button
            onClick={markAsStudied}
            className="flex-1"
            disabled={studiedCards.has(currentCrystal.id)}
          >
            <Check className="h-4 w-4 mr-2" />
            {studiedCards.has(currentCrystal.id) ? 'Studied' : 'Got it! +5 XP'}
          </Button>
        </div>

        <Button
          variant="outline"
          size="icon"
          onClick={nextCard}
          disabled={currentIndex === cards.length - 1}
        >
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      {/* Completion */}
      {studiedCards.size === crystals.length && (
        <Card className="bg-gradient-to-br from-accent/20 to-primary/20 border-accent/30">
          <CardContent className="p-4 text-center">
            <Sparkles className="h-8 w-8 text-accent mx-auto mb-2" />
            <p className="font-medium text-foreground">All Crystals Studied!</p>
            <p className="text-sm text-muted-foreground">
              You earned {xpEarned} XP. Try the quizzes to test your knowledge!
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
